import { defineStore } from 'pinia';
import axios from 'axios';
import { revenueTargetService } from '@/services/revenueTargetService';

/**
 * Revenue Target Store (Phase 3 — 2026-05-12)
 *
 * Backs the Settings → Forecast & doelen form. Holds:
 *
 *   - The curated benchmark list (cached for the session — it's
 *     effectively static)
 *   - The current target row per year (`targetsByYear[2026]`)
 *   - The live monthly preview (`monthlyByYear[2026]`)
 *
 * Loading / error state is tracked per year (not globally) so two
 * concurrent year switches don't fight over a shared flag.
 *
 * The dashboard forecast widget does **not** read from this store —
 * it goes through `dashboardStatsStore.fetchForecast()` directly so
 * the responsibility stays narrow ("forecast = aggregation, target =
 * configuration"). The two are coupled at the data layer, not the
 * client.
 */

const isCanceledError = (error) => {
  if (!error) return false;
  if (axios.isCancel && axios.isCancel(error)) return true;
  if (error.code === 'ERR_CANCELED') return true;
  if (error.name === 'CanceledError' || error.name === 'AbortError') return true;
  return false;
};

const emptyYearState = () => ({
  target: null,
  loading: false,
  error: null,
  saving: false,
  // Live monthly preview (resolved fractions × annual target). Kept
  // separately because we re-fetch on every strategy/benchmark change
  // without persisting the form yet.
  monthly: null,
  monthlyLoading: false,
  monthlyError: null,
});

export const useRevenueTargetStore = defineStore('revenueTarget', {
  state: () => ({
    // Cached benchmark catalogue — populated once and re-used.
    benchmarks: [],
    benchmarksLoading: false,
    benchmarksLoaded: false,
    benchmarksError: null,

    // Per-year detail state, lazily populated.
    yearsState: {},

    // List of years that have a target configured — used by the
    // dashboard year-switcher to mark which years are "set" vs "empty".
    knownYears: [],
    knownYearsLoaded: false,

    // AbortControllers keyed by year (target fetch) and `monthly:<year>`
    // (monthly preview). We need separate controllers because a user
    // can be tweaking the form (monthly refetches) while a save is in
    // flight — those shouldn't cancel each other.
    _controllers: {},
  }),

  getters: {
    /** Get (creating if missing) the per-year sub-state. */
    yearState(state) {
      return (year) => {
        if (year == null) return null;
        return state.yearsState[String(year)] || null;
      };
    },

    benchmarkByKey(state) {
      const map = new Map(state.benchmarks.map((b) => [b.key, b]));
      return (key) => (key ? map.get(key) || null : null);
    },

    defaultBenchmark(state) {
      return state.benchmarks.find((b) => b.is_default) || state.benchmarks[0] || null;
    },
  },

  actions: {
    /** Internal: ensure a year slot exists before mutating it. */
    _ensureYearSlot(year) {
      const key = String(year);
      if (!this.yearsState[key]) {
        this.yearsState[key] = emptyYearState();
      }
      return this.yearsState[key];
    },

    /**
     * Load the benchmark catalogue. No-op after the first successful
     * call — callers can fire-and-forget on form mount.
     */
    async fetchBenchmarks({ force = false } = {}) {
      if (this.benchmarksLoaded && !force) return this.benchmarks;
      this.benchmarksLoading = true;
      this.benchmarksError = null;
      try {
        const list = await revenueTargetService.getBenchmarks();
        this.benchmarks = list;
        this.benchmarksLoaded = true;
        return list;
      } catch (error) {
        if (isCanceledError(error)) return this.benchmarks;
        this.benchmarksError = error?.response?.data?.error || error?.message || 'failed';
        throw error;
      } finally {
        this.benchmarksLoading = false;
      }
    },

    /**
     * Fetch the target row for a year. Resolves to `null` when no
     * target is set (404 swallowed by the service). Calls re-use the
     * cached value unless `force` is true.
     */
    async fetchTarget(year, { force = false } = {}) {
      const slot = this._ensureYearSlot(year);
      if (!force && slot.target !== null) return slot.target;

      const key = `target:${year}`;
      const existing = this._controllers[key];
      if (existing) existing.abort();
      const controller = new AbortController();
      this._controllers[key] = controller;

      slot.loading = true;
      slot.error = null;
      try {
        const target = await revenueTargetService.getByYear(year, {
          signal: controller.signal,
        });
        slot.target = target;
        return target;
      } catch (error) {
        if (isCanceledError(error)) return slot.target;
        slot.error = error?.response?.data?.error || error?.message || 'failed';
        throw error;
      } finally {
        slot.loading = false;
        if (this._controllers[key] === controller) delete this._controllers[key];
      }
    },

    /**
     * Fetch the resolved monthly preview for a year. Always force-
     * refetches because this is what powers the live preview as the
     * user toys with strategy / benchmark / custom inputs.
     */
    async fetchMonthly(year) {
      const slot = this._ensureYearSlot(year);

      const key = `monthly:${year}`;
      const existing = this._controllers[key];
      if (existing) existing.abort();
      const controller = new AbortController();
      this._controllers[key] = controller;

      slot.monthlyLoading = true;
      slot.monthlyError = null;
      try {
        const monthly = await revenueTargetService.getMonthly(year, {
          signal: controller.signal,
        });
        slot.monthly = monthly;
        return monthly;
      } catch (error) {
        if (isCanceledError(error)) return slot.monthly;
        if (error?.response?.status === 404) {
          // No target yet → no monthly to preview. The form should
          // gracefully degrade by computing the preview locally from
          // benchmark fractions.
          slot.monthly = null;
          slot.monthlyError = 'not_found';
          return null;
        }
        slot.monthlyError = error?.response?.data?.error || error?.message || 'failed';
        throw error;
      } finally {
        slot.monthlyLoading = false;
        if (this._controllers[key] === controller) delete this._controllers[key];
      }
    },

    /**
     * Persist the target via PUT (replace semantics). Returns the
     * row from the API so the caller can chain into a monthly refresh.
     */
    async saveTarget(year, body) {
      const slot = this._ensureYearSlot(year);
      slot.saving = true;
      slot.error = null;
      try {
        const target = await revenueTargetService.save(year, body);
        slot.target = target;
        // Keep the year visible in the switcher.
        if (!this.knownYears.includes(year)) {
          this.knownYears = [...this.knownYears, year].sort((a, b) => b - a);
        }
        return target;
      } catch (error) {
        slot.error = error?.response?.data?.error || error?.message || 'failed';
        throw error;
      } finally {
        slot.saving = false;
      }
    },

    /**
     * Partial update — same response shape as `saveTarget`. Used by
     * the live-preview flow where we want to apply only the changed
     * fields without round-tripping the whole row.
     */
    async patchTarget(year, body) {
      const slot = this._ensureYearSlot(year);
      slot.saving = true;
      slot.error = null;
      try {
        const target = await revenueTargetService.patch(year, body);
        slot.target = target;
        if (!this.knownYears.includes(year)) {
          this.knownYears = [...this.knownYears, year].sort((a, b) => b - a);
        }
        return target;
      } catch (error) {
        slot.error = error?.response?.data?.error || error?.message || 'failed';
        throw error;
      } finally {
        slot.saving = false;
      }
    },

    async deleteTarget(year) {
      const slot = this._ensureYearSlot(year);
      slot.saving = true;
      slot.error = null;
      try {
        await revenueTargetService.remove(year);
        slot.target = null;
        slot.monthly = null;
        this.knownYears = this.knownYears.filter((y) => y !== year);
      } catch (error) {
        slot.error = error?.response?.data?.error || error?.message || 'failed';
        throw error;
      } finally {
        slot.saving = false;
      }
    },

    /** Populate `knownYears` from the list endpoint. */
    async fetchKnownYears({ fromYear, toYear } = {}) {
      try {
        const targets = await revenueTargetService.list({ fromYear, toYear });
        this.knownYears = targets.map((t) => t.year).sort((a, b) => b - a);
        this.knownYearsLoaded = true;
        return targets;
      } catch (error) {
        if (isCanceledError(error)) return [];
        throw error;
      }
    },

    /** Wipe state on logout / org-switch. */
    reset() {
      Object.values(this._controllers).forEach((c) => c?.abort?.());
      this._controllers = {};
      this.benchmarks = [];
      this.benchmarksLoaded = false;
      this.benchmarksError = null;
      this.yearsState = {};
      this.knownYears = [];
      this.knownYearsLoaded = false;
    },
  },
});

export default useRevenueTargetStore;
